import {
  Box,
  Grid,
  Typography,
  Divider,
  useMediaQuery,
  Theme,
  Avatar,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { MuiButton, MuiTextField2 } from '@/components';
import { palette } from '@/theme/constants';
import Image from 'next/image';
import { FormikConfig } from './formik';
import { useTranslation } from 'next-i18next';
import { UseFirstRender, UseIntermitence } from '@/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { authSelector, subscriptionSelector } from '@/store/selectors';
import { FetchFileService, cdn_url, fileConverter } from '@/utils';
import { editProfileView, getPaymentMethod, openSubscriptionPopup, updateUserData } from '@/store/actions';
import { getUploadSignedUrl } from '@/store/file/action';
import { useRouter } from 'next/router';
import { ManageSubscription } from '../../ManageSubscription.tsx';
import ChevronLeftIconComponent from '../../../../../public/icons/components/chevron-left';
import SubscriptionSection from '../SubscriptionSection';

export const Profile = () => {
  const { t } = useTranslation();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { status: showPassword, switchStatus: switchShowPassword } = UseIntermitence();
  const { status: showConfirmPassword, switchStatus: switchShowConfirmPassword } = UseIntermitence();
  const [isEditable, setIsEditable] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const { paymentMethod } = useSelector(subscriptionSelector);
  const [imageUrl, setImageUrl] = useState('');
  const [editableField, setEditableField] = useState(null);
  const dispatch = useDispatch();
  const router = useRouter();
  const { user } = useSelector(authSelector);
  const inputRef: any = useRef(null);
  const processFile = (data: any) => {
    dispatch(
      getUploadSignedUrl(
        { file: `${user?.email}/${selectedImage?.name.replace(/\.[^.]+$/, '.webp')}`, type: 'image/webp' },
        async (res: any) => {
          try {
            const convertedImage = await fileConverter(selectedImage);
            const response = await FetchFileService(res?.value?.url?.uploadUrl, 'PUT', convertedImage, 'image/webp');
            if (response?.ok) {
              dispatch(
                updateUserData({
                  ...data,
                  picture: `${user?.email}/${selectedImage?.name.replace(/\.[^.]+$/, '.webp')}`,
                }),
              );
            }
          } catch (error) {}
        },
      ),
    );
  };
  const handleSubmit = (data: any) => {
    if (typeof selectedImage !== 'string' && selectedImage) {
      return processFile(data);
    }
    dispatch(updateUserData(data));
  };

  const handleStartMembership = () => {
    localStorage.setItem('router_history', '/app/profile');
    dispatch(openSubscriptionPopup());
  };

  const handleOnTouched = (key: string) => setTouched({ ...touched, [key]: true });

  const {
    values,
    handleSubmit: formikSubmit,
    handleChange,
    errors,
    touched,
    setTouched,
    setFieldValue,
  } = FormikConfig(t, handleSubmit, user);

  UseFirstRender(() => {
    if (user?.token) {
      for (const val of Object.keys(values || {})) {
        if (val != 'password') {
          setFieldValue(val, user[val]);
        }
      }
      setFieldValue('referralCode', user?.referalCode?.trim()?.toUpperCase());
    }
    if (user?.picture) setImageUrl(`${cdn_url}${user?.picture}`);
  }, [user]);

  const changeInputStatus = (value: string, error: any) => {
    if (value !== '') {
      if (error) return 'error';
      return 'inherit';
    }
    return 'inherit';
  };

  UseFirstRender(() => {
    if (user) {
      dispatch(editProfileView(user.id));
    }
  }, [dispatch]);

  const handleImageEdit = () => {
    inputRef?.current?.click();
  };

  useEffect(() => {
    if (typeof selectedImage !== 'string' && selectedImage) {
      processFile(selectedImage);
    }
    setSelectedImage(null);
  }, [selectedImage]);

  const handleEditClick = (field: any) => {
    setEditableField(editableField === field ? null : field);
  };

  const manageSub = () => {
    if (!showManageSubscription) {
      dispatch(getPaymentMethod({ userId: user?.id }));

      if (paymentMethod) {
        setShowManageSubscription(true);
      }
    } else {
      setShowManageSubscription(false);
    }
  };

  useEffect(() => {
    const hasSocialAuth = !!user?.googleAccessToken || !!user?.facebookAccessToken || !!user?.appleuserAccessToken;

    if (
      !!errors?.name &&
      (hasSocialAuth || errors?.lastname === undefined || errors?.lastname === null) &&
      !!errors?.password &&
      !!errors?.confirm_password
    ) {
      setIsEditable(true);
    }
  }, [
    errors?.name,
    errors?.lastname,
    errors?.password,
    errors?.confirm_password,
    user?.googleAccessToken,
    user?.facebookAccessToken,
    user?.appleuserAccessToken,
  ]);

  return (
    <Box
      position='relative'
      zIndex={1}
      alignContent={'center'}
      bgcolor={isMobile ? '' : 'rgba(241, 247, 250, 1)'}
      borderRadius={'10px'}
      p={2}
      pb={isMobile ? 10 : ''}
      maxWidth={'100%'}
      margin='0 auto'>
      {!showManageSubscription ? (
        <Box display={'flex'} width={'100%'} maxWidth={'521px'}>
          <Box width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} borderRadius={'1.25rem'}>
            <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
              <Box
                borderRadius='50%'
                display='flex'
                justifyContent='center'
                alignItems='center'
                marginBottom='1.25rem'
                width={isMobile ? 90 : 110}
                height={isMobile ? 90 : 110}
                minHeight={isMobile ? 90 : 110}
                minWidth={isMobile ? 90 : 110}
                position='relative'
                sx={{
                  cursor: isEditable ? 'pointer' : 'inherit',
                  '&:hover .edit-icon': {
                    opacity: 1,
                    visibility: 'visible',
                  },
                }}>
                {!selectedImage && !imageUrl ? (
                  <Avatar
                    sx={{
                      bgcolor: '#89D3FF',
                      width: 120,
                      height: 120,
                      fontSize: 50,
                      mr: isMobile ? 1 : 2,
                      color: '#0072CE',
                    }}>
                    {user?.name
                      ?.split(' ')
                      .map((n: any) => n[0])
                      .join('')
                      .toUpperCase()}
                  </Avatar>
                ) : (
                  <Image
                    src={selectedImage ? URL.createObjectURL(selectedImage) : imageUrl}
                    alt='profile image'
                    fill
                    sizes='100%'
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                    quality={80}
                    priority
                  />
                )}

                {/* Edit icon appears on hover */}
                <Box
                  className='edit-icon'
                  position='absolute'
                  bottom={0}
                  right={8}
                  borderRadius='50%'
                  bgcolor='#FFFFFF'
                  width={32}
                  height={32}
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  sx={{
                    opacity: 1,
                  }}>
                  <IconButton
                    size='small'
                    sx={{
                      color: 'white',
                      padding: 0,
                    }}
                    onClick={handleImageEdit}>
                    <Image src={'/icons/edit.svg'} alt='icon' width={20} height={20} color={'#fff'} />
                  </IconButton>
                </Box>

                {/* Hidden input for file upload */}
                <input
                  id='profile-input'
                  className='profile-input'
                  type='file'
                  style={{ display: 'none' }}
                  ref={inputRef}
                  accept='.jpg, .jpeg, .png'
                  onChange={async (e: any) => {
                    setSelectedImage(e?.target?.files?.[0]);
                  }}
                />
              </Box>

              <Grid>
                <Typography fontSize={isMobile ? '1rem' : '1.625rem'} color={palette?.black}>
                  {user?.name} {user?.lastname}{' '}
                </Typography>
              </Grid>
            </Box>
            <Box
              display={'flex'}
              padding={'1.25rem 0'}
              width={'90%'}
              margin={'0 auto'}
              px={isMobile ? '0rem' : '4rem'}
              justifyContent={'space-between'}>
              <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                <Typography variant='h5' fontSize={'2.625rem'} color={palette?.background}>
                  {user?.memoryCount || 0}
                </Typography>
                <Typography fontSize={'1rem'} color={palette?.inputLabelLight} variant='h6'>
                  {t('memories')}
                </Typography>
              </Box>
              <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                <Typography variant='h5' fontSize={'2.625rem'} color={palette?.background}>
                  {user?.draftCount || 0}
                </Typography>
                <Typography fontSize={'1rem'} color={palette?.inputLabelLight} variant='h6'>
                  {t('drafts')}
                </Typography>
              </Box>
              <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                <Typography variant='h5' fontSize={'2.625rem'} color={palette?.background}>
                  {user?.collaboratorCount || 0}
                </Typography>
                <Typography fontSize={'1rem'} color={palette?.inputLabelLight} variant='h6'>
                  {t('collaborators')}
                </Typography>
              </Box>
            </Box>

            <form onSubmit={formikSubmit}>
              <Grid container spacing={isMobile ? 0 : 0} width={'100%'} marginBottom={'0.5rem'}>
                <Grid item xs={12} my={1.25}>
                  <Typography fontSize={'1rem'} mb={'-6px'} color='#000' align='left'>
                    First name
                  </Typography>
                  <MuiTextField2
                    id='name'
                    name='name'
                    fullWidth
                    variant='standard'
                    onBlur={() => handleOnTouched('name')}
                    status={changeInputStatus(values.name, errors.name && touched.name)}
                    onChange={handleChange}
                    value={values.name}
                    disabled={editableField !== 'name'}
                    autoComplete='First name'
                    placeholder='First name'
                    errorMessage={errors.name}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <MuiButton
                            type='button'
                            variant='contained'
                            disabled={editableField === 'name' && !!errors.name}
                            method={() => {
                              const hasErrors = Object.values(errors).some((error) => !!error);

                              if (hasErrors && editableField !== 'name') {
                                return;
                              }
                              if (editableField === 'name') {
                                handleEditClick(null);
                                formikSubmit();
                              } else {
                                handleEditClick('name');
                              }
                            }}
                            sx={{
                              marginBottom: '1.2rem',
                              backgroundColor: '#0072CE',
                              color: 'white',
                              borderRadius: '2rem',
                              boxShadow: 'none',
                              '&:hover': {
                                color: '#000',
                                backgroundColor: 'white',
                                boxShadow: 'none',
                              },
                            }}>
                            <Typography fontSize='0.8rem' color={'inherit'} variant='button'>
                              {editableField === 'name' ? t('save_mayus') : t('edit_mayus')}
                            </Typography>
                          </MuiButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {!isMobile && (
                  <Divider
                    style={{
                      margin: '1rem 0',
                      width: '100%',
                      background: isMobile ? 'transparent' : 'rgba(216, 232, 246, 1)',
                    }}
                  />
                )}
                {/* Last Name Field */}
                <Grid item xs={12} my={1.25}>
                  <Typography fontSize={'1rem'} mb={'-6px'} color='#000' align='left'>
                    Last name
                  </Typography>
                  <MuiTextField2
                    id='lastname'
                    name='lastname'
                    variant='standard'
                    fullWidth
                    onBlur={() => handleOnTouched('lastname')}
                    disabled={editableField !== 'lastname'}
                    status={changeInputStatus(values.lastname, errors.lastname && touched.lastname)}
                    onChange={handleChange}
                    value={values.lastname}
                    autoComplete='lastname'
                    placeholder='lastname'
                    isDarkTheme={false}
                    errorMessage={errors.lastname}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <MuiButton
                            type='button'
                            variant='contained'
                            disabled={editableField === 'lastname' && !!errors.lastname}
                            method={() => {
                              const hasErrors = Object.values(errors).some((error) => !!error);

                              if (hasErrors && editableField !== 'lastname') {
                                return;
                              }

                              if (editableField === 'lastname') {
                                handleEditClick(null);
                                formikSubmit();
                              } else {
                                handleEditClick('lastname');
                              }
                            }}
                            sx={{
                              marginBottom: '1.2rem',
                              backgroundColor: '#0072CE',
                              color: 'white',
                              borderRadius: '2rem',
                              boxShadow: 'none',
                              '&:hover': {
                                color: '#000',
                                backgroundColor: 'white',
                                boxShadow: 'none',
                              },
                            }}>
                            <Typography fontSize='0.8rem' color={'inherit'} variant='button'>
                              {editableField === 'lastname' ? t('save_mayus') : t('edit_mayus')}
                            </Typography>
                          </MuiButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {!isMobile && (
                  <Divider
                    style={{
                      margin: '1rem 0',
                      width: '100%',
                      background: isMobile ? 'transparent' : 'rgba(216, 232, 246, 1)',
                    }}
                  />
                )}

                <Grid item xs={isMobile ? 12 : 12} my={1.25}>
                  <Typography fontSize={'1rem'} mb={'-6px'} color='#000' align='left'>
                    Email
                  </Typography>
                  <MuiTextField2
                    id='email'
                    variant='standard'
                    name='email'
                    fullWidth
                    onBlur={() => {
                      handleOnTouched('email');
                    }}
                    disabled={true}
                    status={changeInputStatus(values.email, errors.email && touched.email)}
                    onChange={handleChange}
                    value={values.email}
                    autoComplete='email'
                    placeholder={'email'}
                    isDarkTheme={false}
                    errorMessage={errors.email}
                  />
                </Grid>
                {!isMobile && (
                  <Divider
                    style={{
                      margin: '1rem 0',
                      width: '100%',
                      background: isMobile ? 'transparent' : 'rgba(216, 232, 246, 1)',
                    }}
                  />
                )}

                <Grid item xs={isMobile ? 12 : 12} my={1.25}>
                  <Typography fontSize={'1rem'} mb={'-6px'} color='#000' align='left'>
                    Password
                  </Typography>
                  <MuiTextField2
                    id='password'
                    name='password'
                    fullWidth
                    onBlur={() => {
                      handleOnTouched('password');
                    }}
                    onChange={handleChange}
                    value={editableField === 'password' ? values.password : '••••••••••••'}
                    autoComplete='password'
                    placeholder='password'
                    variant='standard'
                    isDarkTheme={false}
                    disabled={editableField !== 'password'}
                    type={!showPassword ? 'password' : 'text'}
                    errorMessage={errors.password}
                    status={changeInputStatus(values.password, errors.password && touched.password)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <MuiButton
                            sx={{ marginBottom: '1rem' }}
                            type='button'
                            variant='outline'
                            disabled={
                              !!errors.confirm_password ||
                              Object.values(errors).some((error) => !!error && editableField !== 'password')
                            }
                            method={() => {
                              const hasErrors = Object.values(errors).some((error) => !!error);

                              if (hasErrors && editableField !== 'password') {
                                return;
                              }

                              if (editableField !== 'password') {
                                handleEditClick('password');
                                setShowPasswords(true);
                              } else {
                                handleEditClick(null);
                                setShowPasswords(false);
                                formikSubmit();
                              }
                            }}>
                            <Typography color={'black'} sx={{ textDecoration: 'underline' }} variant='button'>
                              {editableField === 'password' ? 'Save' : 'Change'}
                            </Typography>
                          </MuiButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {!isMobile && (
                  <Divider
                    style={{
                      margin: '1rem 0',
                      width: '100%',
                      background: isMobile ? 'transparent' : 'rgba(216, 232, 246, 1)',
                    }}
                  />
                )}

                {showPasswords && (
                  <>
                    <Grid item xs={isMobile ? 12 : 12} my={1.25}>
                      <Typography fontSize={'1rem'} mb={'-6px'} color='#000' align='left'>
                        Confirm password
                      </Typography>
                      <MuiTextField2
                        id='confirm_password'
                        name='confirm_password'
                        variant='standard'
                        fullWidth
                        onBlur={() => {
                          handleOnTouched('confirm_password');
                        }}
                        disabled={editableField !== 'password'}
                        onChange={handleChange}
                        value={values.confirm_password}
                        autoComplete='new-password'
                        placeholder='confirm_password'
                        isDarkTheme={false}
                        iconMethod={switchShowConfirmPassword}
                        iconHeight={18}
                        iconWidth={18}
                        type={!showConfirmPassword ? 'password' : 'text'}
                        endIcon={showConfirmPassword ? '/icons/eye-black.svg' : '/icons/eye-out-black.svg'}
                        errorMessage={errors.confirm_password}
                        status={changeInputStatus(
                          values.confirm_password,
                          errors.confirm_password && touched.confirm_password,
                        )}
                      />
                    </Grid>
                    {!isMobile && (
                      <Divider
                        style={{
                          margin: '1rem 0',
                          width: '100%',
                          background: isMobile ? 'transparent' : 'rgba(216, 232, 246, 1)',
                        }}
                      />
                    )}
                  </>
                )}
              </Grid>
            </form>

            <SubscriptionSection user={user} manageSub={manageSub} handleStartMembership={handleStartMembership} />
          </Box>
        </Box>
      ) : (
        <Box
          display={'flex'}
          flexDirection={'column'}
          marginTop={'0rem'}
          bgcolor={'#F1F7FA'}
          sx={{ backdropFilter: 'blur(4.5625rem)' }}
          minHeight={!isMobile ? 'calc(100vh - 16.735rem)' : 'calc(100vh - 15.735rem)'}
          height={!isMobile ? '100%' : 'calc(100vh - 13.735rem)'}
          width={isMobile ? '100%' : '600px'}
          minWidth={'50%'}
          padding={'0'}
          borderRadius={'0.625rem'}
          overflow={'hidden'}>
          <Box
            width={'100%'}
            display={'flex'}
            flexDirection={'column'}
            border={`0.063rem solid ${palette.cardBorder}`}
            height={'100%'}
            padding={'0.4rem 2rem'}
            borderRadius={'1.25rem'}
            overflow={'auto'}>
            {/* Back Button (Left-aligned) */}
            <Box margin={'1rem 0'}>
              <Button
                onClick={manageSub}
                startIcon={<ChevronLeftIconComponent color={'#212d6d'} />}
                variant='outlined'
                style={{ borderRadius: '19px', border: `1px solid ${palette.cardBorder}` }}>
                <Typography variant={'button'} color={`#212d6d`}>
                  Back
                </Typography>
              </Button>
            </Box>

            <ManageSubscription paymentMethodResponse={paymentMethod} />
          </Box>
        </Box>
      )}
    </Box>

  );
};
